<script>
import AuthLayout from "@/Layouts/AuthLayout.vue";

export default {
    layout: AuthLayout,
};
</script>

<script setup>
import { Head, Link, useForm } from "@inertiajs/vue3";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandInput from "@/Components/Brand/BrandInput.vue";
import BrandPasswordInput from "@/Components/Brand/BrandPasswordInput.vue";

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: "",
    password: "",
    remember: false,
});

const submit = () => {
    form.transform((data) => ({
        ...data,
        remember: form.remember ? "on" : "",
    })).post(route("login"), {
        onFinish: () => form.reset("password"),
    });
};
</script>

<template>
    <Head title="Log In" />

    <div class="flex w-full flex-col items-center">
        <!-- Headline over the panel art (mobile) / logo + headline over the photo band (desktop) -->
        <div class="flex w-full items-center gap-6 pl-[18px] md:w-auto md:pl-0">
            <img
                :src="asset('images/brand/logo-mark.png')"
                alt="Gift Hero"
                class="hidden size-[75px] shrink-0 object-contain md:block"
            />
            <h1
                class="font-brand-display text-[30px] font-black italic uppercase leading-none tracking-[-0.02em] text-white md:text-[32px]"
            >
                Welcome back,<br />gift hero!
            </h1>
        </div>

        <form
            class="mt-[38px] w-full max-w-[408px] rounded-brand bg-white p-[18px] shadow-[0px_30px_55px_0px_rgba(0,0,0,0.25)] sm:p-9 md:mt-7"
            @submit.prevent="submit"
        >
            <div
                v-if="status"
                class="mb-5 font-brand text-sm font-medium text-green-600"
            >
                {{ status }}
            </div>

            <div class="space-y-[22px]">
                <div>
                    <label
                        for="email"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >E-Mail Address</label
                    >
                    <BrandInput
                        id="email"
                        v-model="form.email"
                        type="email"
                        required
                        autofocus
                        autocomplete="email"
                    />
                    <p
                        v-if="form.errors.email"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.email }}
                    </p>
                </div>

                <div>
                    <label
                        for="password"
                        class="mb-0.5 block font-brand text-[15px] font-bold leading-[19px] text-[#3c3938]"
                        >Password</label
                    >
                    <BrandPasswordInput
                        id="password"
                        v-model="form.password"
                        required
                        autocomplete="current-password"
                    />
                    <p
                        v-if="form.errors.password"
                        class="mt-1.5 text-[13px] text-brand-red"
                    >
                        {{ form.errors.password }}
                    </p>
                    <p v-if="canResetPassword" class="mt-2 text-right">
                        <Link
                            :href="route('password.request')"
                            class="font-brand text-[13px] font-medium text-[#3c3938] underline hover:opacity-80"
                            >Forgot Password?</Link
                        >
                    </p>
                </div>
            </div>

            <BrandButton
                type="submit"
                class="mt-8 h-11 w-full text-[15px]"
                :class="{ 'pointer-events-none opacity-60': form.processing }"
                :disabled="form.processing"
            >
                Log In
            </BrandButton>
        </form>

        <p class="mt-7 font-brand text-[15px] text-white md:mt-6">
            <span class="font-semibold">Not Registered?</span>
            <Link
                :href="route('register')"
                class="ml-1.5 !text-white underline hover:opacity-80"
                >Create an Account</Link
            >
        </p>
    </div>
</template>
